/* eslint-disable react-refresh/only-export-components */
import {createContext, useCallback, useContext, useEffect, useState} from "react";

const SIDE_PANEL_STORAGE_KEY = "pecunio-sidepanel-open";
const SELECTED_AGENT_STORAGE_KEY = "pecunio-selected-agent";

function getInitialSidePanelOpen(): boolean {
    if (typeof window === "undefined") return true;
    const stored = window.localStorage.getItem(SIDE_PANEL_STORAGE_KEY);
    if (stored === "true") return true;
    if (stored === "false") return false;
    return window.innerWidth >= 1024;
}

function getInitialSelectedAgentSlug(): string | null {
    if (typeof window === "undefined") return null;
    const stored = window.localStorage.getItem(SELECTED_AGENT_STORAGE_KEY);
    return stored || null;
}

interface ChatContextValue {
    /** The active dashboard tab ("summary" | "forecast" | "cashflow") */
    activeTab: string;
    setActiveTab: (tab: string) => void;
    /** Number of unread agent messages since last user interaction */
    unreadCount: number;
    incrementUnread: () => void;
    resetUnread: () => void;
    /** Whether the chat panel is currently visible (legacy — kept for compat) */
    isChatVisible: boolean;
    setChatVisible: (visible: boolean) => void;
    /** true = chat is docked in side panel, false = floating window */
    chatDocked: boolean;
    setChatDocked: (v: boolean) => void;
    /** Whether the side panel is expanded (true) or collapsed to rail only */
    sidePanelOpen: boolean;
    setSidePanelOpen: (v: boolean) => void;
    /** Whether the main content area is in fullscreen overlay mode */
    contentFullscreen: boolean;
    setContentFullscreen: (v: boolean) => void;
    /** Whether the chat panel is in fullscreen overlay mode */
    chatFullscreen: boolean;
    setChatFullscreen: (v: boolean) => void;
    /** User-selected agent that stays fixed until changed manually */
    selectedAgentSlug: string | null;
    setSelectedAgentSlug: (slug: string | null) => void;
}

const ChatContext = createContext<ChatContextValue | null>(null);

export function ChatProvider({children}: {children: React.ReactNode}) {
    const [activeTab, setActiveTab] = useState("summary");
    const [unreadCount, setUnreadCount] = useState(0);
    const [isChatVisible, setChatVisible] = useState(false);
    const [chatDocked, setChatDocked] = useState(true);
    const [sidePanelOpen, setSidePanelOpen] = useState(getInitialSidePanelOpen);
    const [contentFullscreen, setContentFullscreen] = useState(false);
    const [chatFullscreen, setChatFullscreen] = useState(false);
    const [selectedAgentSlug, setSelectedAgentSlug] = useState<string | null>(getInitialSelectedAgentSlug);

    const incrementUnread = useCallback(() => setUnreadCount((c) => c + 1), []);
    const resetUnread = useCallback(() => setUnreadCount(0), []);

    useEffect(() => {
        if (typeof window === "undefined") return;
        window.localStorage.setItem(SIDE_PANEL_STORAGE_KEY, String(sidePanelOpen));
    }, [sidePanelOpen]);

    useEffect(() => {
        if (typeof window === "undefined") return;
        if (selectedAgentSlug) {
            window.localStorage.setItem(SELECTED_AGENT_STORAGE_KEY, selectedAgentSlug);
        } else {
            window.localStorage.removeItem(SELECTED_AGENT_STORAGE_KEY);
        }
    }, [selectedAgentSlug]);

    return (
        <ChatContext.Provider
            value={{
                activeTab,
                setActiveTab,
                unreadCount,
                incrementUnread,
                resetUnread,
                isChatVisible,
                setChatVisible,
                chatDocked,
                setChatDocked,
                sidePanelOpen,
                setSidePanelOpen,
                contentFullscreen,
                setContentFullscreen,
                chatFullscreen,
                setChatFullscreen,
                selectedAgentSlug,
                setSelectedAgentSlug,
            }}
        >
            {children}
        </ChatContext.Provider>
    );
}

export function useChatContext(): ChatContextValue {
    const ctx = useContext(ChatContext);
    if (!ctx) {
        throw new Error("useChatContext must be used within a ChatProvider");
    }
    return ctx;
}
